/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSpring, animated, easings } from 'react-spring';
import ResizeObserver from 'react-resize-observer';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import EntryPointContent from '../components/presentational/EntryPointContent';
import CalendarCreateContent from '../components/presentational/CalendarCreateContent';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: ${(props) => (props.hideOverflow ? 'hidden' : 'visible')};
`;

const CloseButton = styled('button')`
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9E9E9E;
  transition: color 0.2s ease;
  z-index: 1000;

  &:hover {
    color: #666;
  }
`;

const SelectCalendarModal = ({
  closeModal,
}) => {
  // State for slide management
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [hideOverflow, setHideOverflow] = useState(true);

  // Determine which slide to show
  const showSlide1 = currentSlide === 0;
  const showSlide2 = currentSlide === 1;

  // Animation for slide 1
  const slide1Animation = useSpring({
    opacity: showSlide1 ? 1 : 0,
    transform: showSlide1 ? 'translateX(0%)' : 'translateX(-100%)',
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    immediate: isResizing,
    onRest: () => {
      if (showSlide1) {
        setHideOverflow(false);
      }
    },
  });

  // Animation for slide 2
  let slide2Transform;
  if (showSlide2) {
    slide2Transform = 'translateX(0%)';
  } else if (currentSlide < 1) {
    slide2Transform = 'translateX(100%)';
  } else {
    slide2Transform = 'translateX(-100%)';
  }

  const slide2Animation = useSpring({
    opacity: showSlide2 ? 1 : 0,
    transform: slide2Transform,
    config: {
      duration: 300,
      easing: easings?.easeInOutQuad,
    },
    immediate: isResizing,
    onRest: () => {
      if (showSlide2) {
        setHideOverflow(false);
      }
    },
  });

  // Handle navigation to next slide
  const handleNext = () => {
    setHideOverflow(true);
    if (currentSlide < 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  // Handle navigation back
  const handleBack = () => {
    setHideOverflow(true);
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  return (
    <ModalContainer hideOverflow={hideOverflow}>
      <CloseButton onClick={closeModal}>
        <IcomoonReact
          iconSet={iconSet}
          size={14}
          icon='remove'
        />
      </CloseButton>

      <ResizeObserver
        onResize={() => {
          setIsResizing(true);
          setTimeout(() => {
            setIsResizing(false);
          }, 300);
        }}
      />

      {showSlide1 && (
        <animated.div style={slide1Animation}>
          <EntryPointContent isModal closeModal={closeModal} onNext={handleNext} />
        </animated.div>
      )}

      {showSlide2 && (
        <animated.div style={slide2Animation}>
          <CalendarCreateContent isModal onBack={handleBack} closeModal={closeModal} />
        </animated.div>
      )}
    </ModalContainer>
  );
};

SelectCalendarModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default SelectCalendarModal;
